import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ClockCounterClockwise, DownloadSimple, ArrowClockwise } from "@phosphor-icons/react";
import { downloadCsv } from "@/lib/csv";
import { formatInTimeZone } from "@/lib/timezone";
import ReportFilters, {
  defaultDateRange, ReportPagination, REPORT_PAGE_SIZE,
} from "@/components/reports/ReportFilters";

export default function Reports() {
  const { user } = useAuth();
  const accountTz = user?.timezone || "UTC";
  const defaults = defaultDateRange(1, accountTz);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [applied, setApplied] = useState(defaults);
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [timezone, setTimezone] = useState(accountTz);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit", {
        params: {
          date_from: applied.dateFrom,
          date_to: applied.dateTo,
          page,
          page_size: REPORT_PAGE_SIZE,
        },
      });
      if (Array.isArray(data)) {
        setLogs(data);
        setTotal(data.length);
      } else {
        setLogs(data?.items || []);
        setTotal(data?.total || 0);
        if (data?.timezone) setTimezone(data.timezone);
      }
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied, page]);

  useEffect(() => { load(); }, [load]);

  const fmt = (d) => formatInTimeZone(d, timezone || accountTz);

  const exportCsv = () => {
    if (!logs.length) {
      toast.error("Nothing to export");
      return;
    }
    downloadCsv("audit-logs.csv", [
      { label: "Time", value: (r) => fmt(r.created_at) },
      { label: "Actor", value: (r) => r.actor_email || "system" },
      { label: "Action", value: (r) => r.action || "" },
      { label: "Entity", value: (r) => r.entity_type || "" },
      { label: "Entity ID", value: (r) => r.entity_id || "" },
    ], logs);
  };

  return (
    <div data-testid="audit-logs-page">
      <PageHeader
        title="Audit logs"
        subtitle={`Tenant, pricing, wallet and account operations. Times in ${timezone || accountTz}.`}
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-lg" onClick={exportCsv} disabled={!logs.length}
            data-testid="audit-export-btn">
            <DownloadSimple className="w-4 h-4 mr-1.5" weight="bold" /> Export CSV
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={load} data-testid="audit-refresh-btn">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </PageHeader>

      <ReportFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        onApply={() => {
          setPage(1);
          setApplied({ dateFrom, dateTo });
        }}
        onClear={() => {
          const d = defaultDateRange(1, accountTz);
          setDateFrom(d.dateFrom); setDateTo(d.dateTo);
          setPage(1);
          setApplied(d);
        }}
        applying={loading}
        testid="audit-filters"
        timezone={timezone || accountTz}
      />

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : logs.length === 0 ? (
        <EmptyState
          testid="audit-empty"
          icon={ClockCounterClockwise}
          title="No audit events yet"
          description="Tenant, pricing and wallet operations are recorded here."
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="audit-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} className="hover:bg-muted/40" data-testid={`audit-row-${l.id}`}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(l.created_at)}</TableCell>
                  <TableCell className="text-sm">{l.actor_email || "system"}</TableCell>
                  <TableCell className="font-medium text-sm">{l.action}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.entity_type}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ReportPagination
            page={page}
            pageSize={REPORT_PAGE_SIZE}
            total={total}
            onPage={setPage}
            testid="audit-pagination"
          />
        </div>
      )}
    </div>
  );
}
